import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Save, ArrowLeft, Search, User, Building2, Users } from "lucide-react";
import AsyncCreatableSelect from "react-select/async-creatable";
import { toast } from "react-hot-toast";
import { clientService } from "../../services/clientService";
import { leadService } from "../../services/leadService";
import { formatSSN, formatEIN } from "../../services/helperService";
import * as constantValues from "../../constants/constants";
import DeletableAsyncOption from "./../deletableAsyncOption";

const PERSONAL_SERVICE_NAMES = [
  "Personal Taxes",
  "POA",
  "New Company",
  "Other Services",
];

const CreateLead = ({ userPermission, onLeadCreated }) => {
  const navigate = useNavigate();
  const [leadType, setLeadType] = useState("personal");
  const [loading, setLoading] = useState(false);
  const [idLookup, setIdLookup] = useState("");
  const [nameSearch, setNameSearch] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchError, setSearchError] = useState("");
  const [nameSearchLoading, setNameSearchLoading] = useState(false);
  const [nameSearchResults, setNameSearchResults] = useState([]);
  const [nameSearchError, setNameSearchError] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    company_name: "",
    ein: "",
    trello_card: "",
    trello_card_company: "",
    industry: "",
    status: "Open",
    services: [],
    service_ids: [],
    services_company: [],
    services_company_ids: [],
    accountant: "",
    email: "",
    secondary_email: "",
    phone: "",
    additional_phones: "",
    contact_method: "",
    international: "",
    referral: "",
    refer_partner_id: "",
    refer_partner_name: "",
    sales_rep_id: "",
    sales_rep_name: "",
    language: "",
  });
  const [salesRepDefaults, setSalesRepDefaults] = useState([]);
  const [deletingSalesRepId, setDeletingSalesRepId] = useState(null);
  const [referPartnerDefaults, setReferPartnerDefaults] = useState([]);
  const [deletingReferPartnerId, setDeletingReferPartnerId] = useState(null);
  const [serviceDefaults, setServiceDefaults] = useState([]);
  const [deletingServiceId, setDeletingServiceId] = useState(null);
  // const serviceOptions = constantValues.serviceOptions.map((s) => ({
  //   value: s.value,
  //   label: s.label,
  // }));

  const digitsOnly = useMemo(
    () => (idLookup || "").replace(/\D+/g, ""),
    [idLookup],
  );

  const showPersonalFields = leadType !== "business";
  const showBusinessFields = leadType !== "personal";
  const isBusinessOnly = leadType === "business";
  const companyFieldsRequired =
    isBusinessOnly || (leadType === "both" && Boolean(formData.company_name));

  useEffect(() => {
    if (digitsOnly.length !== 9) {
      setSearchResults([]);
      setSearchError("");
      setSearchLoading(false);
      return;
    }

    let cancelled = false;
    setSearchLoading(true);
    setSearchError("");

    const t = setTimeout(async () => {
      try {
        const results = await clientService.getClients(digitsOnly, 0);
        if (!cancelled) setSearchResults(results?.items || []);
      } catch (e) {
        if (!cancelled) setSearchError("Couldn't search right now.");
        console.error("SSN/EIN search error", e);
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [digitsOnly]);

  useEffect(() => {
    if (!nameSearch.trim()) {
      setNameSearchResults([]);
      setNameSearchError("");
      setNameSearchLoading(false);
      return;
    }

    let cancelled = false;
    setNameSearchLoading(true);
    setNameSearchError("");

    const t = setTimeout(async () => {
      try {
        const results = await clientService.getClients(nameSearch, 0);
        if (!cancelled) setNameSearchResults(results?.items || []);
      } catch (e) {
        if (!cancelled) setNameSearchError("Couldn't search right now.");
        console.error("Name search error", e);
      } finally {
        if (!cancelled) setNameSearchLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [nameSearch]);

  useEffect(() => {
    (async () => {
      try {
        const [reps] = await Promise.all([clientService.getSalesReps("")]);
        setSalesRepDefaults(reps.map((r) => ({ value: r.id, label: r.name })));
        const [refs] = await Promise.all([leadService.getReferPartners("")]);
        setReferPartnerDefaults(
          refs.map((r) => ({ value: r.id, label: r.name })),
        );
        const svcs = await leadService.getServices("");
        setServiceDefaults(svcs.map((r) => ({ value: r.id, label: r.name })));
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const loadSalesRepOptions = async (inputValue) => {
    const rows = await clientService.getSalesReps(inputValue);
    return rows.map((r) => ({ value: r.id, label: r.name }));
  };
  const loadReferPartnerOptions = async (inputValue) => {
    const rows = await leadService.getReferPartners(inputValue);
    return rows.map((r) => ({ value: r.id, label: r.name }));
  };

  const loadServiceOptions = async (inputValue) => {
    const rows = await leadService.getServices(inputValue);
    return rows.map((r) => ({ value: r.id, label: r.name }));
  };

  const loadPersonalServiceOptions = async (inputValue) => {
    const rows = await loadServiceOptions(inputValue);
    return rows.filter((r) => PERSONAL_SERVICE_NAMES.includes(r.label));
  };

  const debounce = (fn, delay = 300) => {
    let t;
    return (...args) =>
      new Promise((resolve) => {
        clearTimeout(t);
        t = setTimeout(async () => resolve(await fn(...args)), delay);
      });
  };
  const debouncedLoadSales = useMemo(() => debounce(loadSalesRepOptions), []);
  const debouncedLoadRefer = useMemo(
    () => debounce(loadReferPartnerOptions),
    [],
  );
  const debouncedLoadService = useMemo(() => debounce(loadServiceOptions), []);
  const debouncedLoadPersonalService = useMemo(
    () => debounce(loadPersonalServiceOptions),
    [],
  );

  const personalServiceDefaults = useMemo(
    () => serviceDefaults.filter((o) => PERSONAL_SERVICE_NAMES.includes(o.label)),
    [serviceDefaults],
  );

  const selectedSalesRepOption = useMemo(() => {
    if (!formData.sales_rep_id) return null;

    const matched = salesRepDefaults.find(
      (option) => String(option.value) === String(formData.sales_rep_id),
    );
    if (matched) return matched;
    if (!formData.sales_rep_name) return null;

    return { value: formData.sales_rep_id, label: formData.sales_rep_name };
  }, [formData.sales_rep_id, formData.sales_rep_name, salesRepDefaults]);

  const salesRepOptions = useMemo(() => {
    if (!selectedSalesRepOption) return salesRepDefaults;

    const selectedExists = salesRepDefaults.some(
      (option) => String(option.value) === String(selectedSalesRepOption.value),
    );
    return selectedExists
      ? salesRepDefaults
      : [selectedSalesRepOption, ...salesRepDefaults];
  }, [selectedSalesRepOption, salesRepDefaults]);

  const selectedServiceValues = useMemo(
    () =>
      (formData.service_ids || []).map((id) => {
        const found = serviceDefaults.find(
          (o) => String(o.value) === String(id),
        );
        return found || { value: id, label: String(id) };
      }),
    [formData.service_ids, serviceDefaults],
  );

  const selectedCompanyServiceValues = useMemo(
    () =>
      (formData.services_company_ids || []).map((id) => {
        const found = serviceDefaults.find(
          (o) => String(o.value) === String(id),
        );
        return found || { value: id, label: String(id) };
      }),
    [formData.services_company_ids, serviceDefaults],
  );

  const selectedReferPartnerOption = useMemo(() => {
    if (!formData.refer_partner_id) return null;

    const matched = referPartnerDefaults.find(
      (option) => String(option.value) === String(formData.refer_partner_id),
    );
    if (matched) return matched;
    if (!formData.refer_partner_name) return null;

    return {
      value: formData.refer_partner_id,
      label: formData.refer_partner_name,
    };
  }, [
    formData.refer_partner_id,
    formData.refer_partner_name,
    referPartnerDefaults,
  ]);

  const referPartnersOptions = useMemo(() => {
    if (!selectedReferPartnerOption) return referPartnerDefaults;

    const selectedExists = referPartnerDefaults.some(
      (option) =>
        String(option.value) === String(selectedReferPartnerOption.value),
    );
    return selectedExists
      ? referPartnerDefaults
      : [selectedReferPartnerOption, ...referPartnerDefaults];
  }, [selectedReferPartnerOption, referPartnerDefaults]);

  const handleDeleteServiceOption = useCallback(async (option) => {
    if (!option?.value) return;
    if (
      !window.confirm(
        `Delete service "${option.label}"? This will unassign it from any lead.`,
      )
    ) {
      return;
    }
    try {
      setDeletingServiceId(option.value);
      await leadService.deleteService(option.value);
      setServiceDefaults((prev) =>
        prev.filter((item) => item.value !== option.value),
      );
      setFormData((prev) => ({
        ...prev,
        service_ids: (prev.service_ids || []).filter(
          (id) => String(id) !== String(option.value),
        ),
        services: (prev.services || []).filter((name) => name !== option.label),
        services_company_ids: (prev.services_company_ids || []).filter(
          (id) => String(id) !== String(option.value),
        ),
        services_company: (prev.services_company || []).filter(
          (name) => name !== option.label,
        ),
      }));
      toast.success(`Service "${option.label}" deleted`);
    } catch (error) {
      console.error("Delete service error", error);
      toast.error("Could not delete Service");
    } finally {
      setDeletingServiceId(null);
    }
  }, []);

  const handleDeleteSalesRepOption = useCallback(async (option) => {
    if (!option?.value) return;
    if (
      !window.confirm(
        `Delete sales rep "${option.label}"? This will unassign it from any lead or client.`,
      )
    ) {
      return;
    }
    try {
      setDeletingSalesRepId(option.value);
      await clientService.deleteSalesRep(option.value);
      setSalesRepDefaults((prev) =>
        prev.filter((item) => item.value !== option.value),
      );
      setFormData((prev) => {
        if (prev.sales_rep_id !== option.value) return prev;
        return { ...prev, sales_rep_id: null, sales_rep_name: "" };
      });
      toast.success(`Sales rep "${option.label}" deleted`);
    } catch (error) {
      console.error("Delete sales rep error", error);
      toast.error("Could not delete Sales Rep");
    } finally {
      setDeletingSalesRepId(null);
    }
  }, []);

  const handleDeleteReferPartnerOption = useCallback(async (option) => {
    if (!option?.value) return;
    if (
      !window.confirm(
        `Delete refer partner "${option.label}"? This will unassign it from any lead.`,
      )
    ) {
      return;
    }
    try {
      setDeletingReferPartnerId(option.value);
      await leadService.deleteReferPartner(option.value);
      setReferPartnerDefaults((prev) =>
        prev.filter((item) => item.value !== option.value),
      );
      setFormData((prev) => {
        if (prev.refer_partner_id !== option.value) return prev;
        return { ...prev, refer_partner_id: null, refer_partner_name: "" };
      });
      toast.success(`Refer partner "${option.label}" deleted`);
    } catch (error) {
      console.error("Delete refer partner error", error);
      toast.error("Could not delete Refer Partner");
    } finally {
      setDeletingReferPartnerId(null);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "company_name" && !value) {
      setFormData((prev) => ({
        ...prev,
        company_name: "",
        ein: "",
        trello_card_company: "",
        services_company: [],
        services_company_ids: [],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const validateForm = () => {
    if (!formData.name) {
      toast.error(isBusinessOnly ? "Contact Name is required" : "Name is required");
      return false;
    }

    if (showPersonalFields) {
      if (!formData.trello_card) {
        toast.error("Trello Card Name is required");
        return false;
      }
    }

    if (isBusinessOnly && !formData.company_name) {
      toast.error("Name is required");
      return false;
    }

    if (companyFieldsRequired && !formData.trello_card_company) {
      toast.error("Trello Card Company Name is required");
      return false;
    }

    if (!formData.email) {
      toast.error("Email is required");
      return false;
    }
    if (!formData.phone) {
      toast.error("Phone number is required");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const sharedFields = {
        industry: formData.industry || null,
        status: formData.status || null,
        accountant: formData.accountant || null,
        email: formData.email,
        secondary_email: formData.secondary_email || null,
        phone: formData.phone,
        additional_phones: formData.additional_phones || null,
        contact_method: formData.contact_method || null,
        international: formData.international || null,
        referral: formData.referral || null,
        refer_partner_id: formData.refer_partner_id || null,
        refer_partner_name: formData.refer_partner_name || null,
        sales_rep_id: formData.sales_rep_id || null,
        sales_rep_name: formData.sales_rep_name || null,
        language: formData.language || null,
      };

      const individualLead = {
        lead: {
          ...sharedFields,
          type: "personal",
          name: formData.name,
          company_name: formData.company_name || null,
          trello_card: formData.trello_card || null,
          services: formData.services || null,
          service_ids: formData.service_ids || [],
        },
      };

      const companyLead = {
        lead: {
          ...sharedFields,
          type: "business",
          name: formData.company_name || null,
          company_name: formData.name || null,
          ein: formData.ein || null,
          trello_card: formData.trello_card_company || null,
          services: formData.services_company || null,
          service_ids: formData.services_company_ids || [],
        },
      };

      const createIndividual = showPersonalFields;
      const createCompany =
        isBusinessOnly || (leadType === "both" && Boolean(formData.company_name));

      if (createIndividual && createCompany) {
        const [lead1, lead2] = await Promise.all([
          leadService.createLead(individualLead),
          leadService.createLead(companyLead),
        ]);

        onLeadCreated(lead1);
        onLeadCreated(lead2);
        toast.success("2 leads created successfully!");
      } else if (createCompany) {
        const newLead = await leadService.createLead(companyLead);
        onLeadCreated(newLead);
        toast.success("Lead created successfully!");
      } else {
        const newLead = await leadService.createLead(individualLead);
        onLeadCreated(newLead);
        toast.success("Lead created successfully!");
      }

      navigate("/leads");
    } catch (error) {
      console.error("Create lead error: ", error);
      toast.error("Failed to create lead");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto animate-fadeIn">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate("/leads")}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Leads
        </button>

        <h1 className="text-3xl font-bold text-gray-900 mb-2">Add New Lead</h1>
        <p className="text-gray-600">Create a new lead profile</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Lead Type Selection */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Lead Type
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              type="button"
              onClick={() => setLeadType("personal")}
              className={`p-4 border-2 rounded-lg text-left transition-all ${
                leadType === "personal"
                  ? "border-primary-500 bg-primary-50 text-primary-700"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center mb-2">
                <User className="h-5 w-5 mr-2" />
                <span className="font-medium">Personal</span>
              </div>
              <p className="text-sm text-gray-600">
                Individual lead or contact
              </p>
            </button>

            <button
              type="button"
              onClick={() => setLeadType("business")}
              className={`p-4 border-2 rounded-lg text-left transition-all ${
                leadType === "business"
                  ? "border-primary-500 bg-primary-50 text-primary-700"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center mb-2">
                <Building2 className="h-5 w-5 mr-2" />
                <span className="font-medium">Business</span>
              </div>
              <p className="text-sm text-gray-600">Business or organization</p>
            </button>

            <button
              type="button"
              onClick={() => setLeadType("both")}
              className={`p-4 border-2 rounded-lg text-left transition-all ${
                leadType === "both"
                  ? "border-primary-500 bg-primary-50 text-primary-700"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center mb-2">
                <Users className="h-5 w-5 mr-2" />
                <span className="font-medium">Personal + Business</span>
              </div>
              <p className="text-sm text-gray-600">
                Create both an individual and a business lead
              </p>
            </button>
          </div>
        </div>

        {/* Client Existence */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Client Existence
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center border border-gray-300 rounded-lg px-3 py-2 w-full">
                <Search className="h-4 w-4 text-canopy-textMuted mr-2" />
                <input
                  type="number"
                  placeholder={`Search by SSN or EIN`}
                  value={idLookup}
                  onChange={(e) => setIdLookup(e.target.value)}
                  maxLength={9}
                  className="flex-1 outline-none appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
                />
                {idLookup && (
                  <button
                    onClick={() => setIdLookup("")}
                    className="ml-2 text-gray-400 hover:text-gray-600 focus:outline-none"
                  >
                    ✕
                  </button>
                )}
              </div>

              <div className="mt-2 text-sm">
                {digitsOnly.length === 0 && (
                  <span className="text-gray-500">
                    Enter 9 digits to search
                  </span>
                )}
                {digitsOnly.length > 0 && digitsOnly.length < 9 && (
                  <span className="text-gray-500">Keep typing…</span>
                )}
                {digitsOnly.length > 9 && (
                  <span className="text-red-600">
                    Too many digits (must be 9)
                  </span>
                )}
                {searchLoading && (
                  <span className="text-gray-500">Searching...</span>
                )}
                {!searchLoading && searchError && (
                  <span className="text-red-600">{searchError}</span>
                )}
              </div>

              {/* Results (only when 9 digits) */}
              {digitsOnly.length === 9 && !searchLoading && !searchError && (
                <div className="mt-3">
                  {searchResults.length === 0 ? (
                    <div className="text-sm text-gray-500">No matches</div>
                  ) : (
                    <ul className="divide-y divide-gray-100 border rounded-md mt-2">
                      {searchResults.map((c) => {
                        const isBusiness = c.client.client_type === "business";
                        const idText = isBusiness
                          ? c.business?.ein
                            ? `EIN: ${formatEIN(c.business.ein)}`
                            : "EIN: —"
                          : c.person?.ssn
                            ? `SSN: ${formatSSN(c.person.ssn)}`
                            : "SSN: —";
                        return (
                          <li
                            key={c.id}
                            className="p-3 flex items-center justify-between"
                          >
                            <div>
                              <div className="font-medium text-gray-900">
                                {c.client.name}
                                <span className="ml-2 text-xs px-2 py-0.5 rounded-full border">
                                  {isBusiness ? "Business" : "Person"}
                                </span>
                              </div>
                              <div className="text-sm text-gray-600">
                                {c.client.email} · {c.client.phone}
                              </div>
                              <div className="text-xs text-gray-500">
                                {idText}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => navigate(`/client/${c.id}`)}
                              className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                            >
                              Open
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center border border-gray-300 rounded-lg px-3 py-2 w-full">
                <Search className="h-4 w-4 text-canopy-textMuted mr-2" />
                <input
                  type="text"
                  placeholder="Search by Name"
                  value={nameSearch}
                  onChange={(e) => setNameSearch(e.target.value)}
                  className="flex-1 outline-none"
                />
                {nameSearch && (
                  <button
                    type="button"
                    onClick={() => setNameSearch("")}
                    className="ml-2 text-gray-400 hover:text-gray-600 focus:outline-none"
                  >
                    ✕
                  </button>
                )}
              </div>

              <div className="mt-2 text-sm">
                {!nameSearch && (
                  <span className="text-gray-500">Enter a name to search</span>
                )}
                {nameSearchLoading && (
                  <span className="text-gray-500">Searching...</span>
                )}
                {!nameSearchLoading && nameSearchError && (
                  <span className="text-red-600">{nameSearchError}</span>
                )}
              </div>

              {nameSearch.trim() && !nameSearchLoading && !nameSearchError && (
                <div className="mt-3">
                  {nameSearchResults.length === 0 ? (
                    <div className="text-sm text-gray-500">No matches</div>
                  ) : (
                    <ul className="divide-y divide-gray-100 border rounded-md mt-2">
                      {nameSearchResults.map((c) => {
                        const isBusiness = c.client.client_type === "business";
                        return (
                          <li
                            key={c.id}
                            className="p-3 flex items-center justify-between"
                          >
                            <div>
                              <div className="font-medium text-gray-900">
                                {c.client.name}
                                <span className="ml-2 text-xs px-2 py-0.5 rounded-full border">
                                  {isBusiness ? "Business" : "Person"}
                                </span>
                              </div>
                              <div className="text-sm text-gray-600">
                                {c.client.email} · {c.client.phone}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => navigate(`/client/${c.id}`)}
                              className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                            >
                              Open
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Lead Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Lead Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-red-600 mb-2">
                {isBusinessOnly ? "Contact Name" : "Name"} *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>
            {showBusinessFields && (
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isBusinessOnly ? "text-red-600" : "text-gray-700"
                  }`}
                >
                  {isBusinessOnly ? "Name" : "Company Name"}
                  {isBusinessOnly ? " *" : ""}
                </label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required={isBusinessOnly}
                />
              </div>
            )}
            {showPersonalFields && (
              <div>
                <label className="block text-sm font-medium text-red-600 mb-2">
                  Trello Card Name *
                </label>
                <input
                  type="text"
                  name="trello_card"
                  value={formData.trello_card}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
            )}
            {showBusinessFields && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    EIN
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    name="ein"
                    value={formData.ein}
                    onChange={(e) => {
                      const digits = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 9);
                      setFormData((prev) => ({ ...prev, ein: digits }));
                    }}
                    maxLength={9}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      companyFieldsRequired ? "text-red-600" : "text-gray-700"
                    }`}
                  >
                    Trello Card Company Name{companyFieldsRequired ? " *" : ""}
                  </label>
                  <input
                    type="text"
                    name="trello_card_company"
                    value={formData.trello_card_company}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required={companyFieldsRequired}
                  />
                </div>
              </>
            )}
            {showBusinessFields && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Industry
                </label>
                <input
                  type="text"
                  name="industry"
                  value={formData.industry}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Select status</option>
                {constantValues.leadStatusOptions.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            {showPersonalFields && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Services
                </label>
                <AsyncCreatableSelect
                  isClearable
                  isMulti
                  cacheOptions
                  defaultOptions={personalServiceDefaults}
                  loadOptions={debouncedLoadPersonalService}
                  components={{ Option: DeletableAsyncOption }}
                  userPermission={userPermission}
                  onDeleteOption={handleDeleteServiceOption}
                  deletingOptionId={deletingServiceId}
                  value={selectedServiceValues}
                  onChange={(selected) =>
                    setFormData((prev) => ({
                      ...prev,
                      service_ids: (selected || []).map((o) => o.value),
                      services: (selected || []).map((o) => o.label),
                    }))
                  }
                  onCreateOption={async (name) => {
                    try {
                      const created = await leadService.createService(name);
                      const opt = { value: created.id, label: created.name };
                      setServiceDefaults((prev) => [...prev, opt]);
                      setFormData((prev) => ({
                        ...prev,
                        service_ids: [...(prev.service_ids || []), created.id],
                        services: [...(prev.services || []), created.name],
                      }));
                    } catch (e) {
                      console.error(e);
                      toast.error("Could not create Service");
                    }
                  }}
                  closeMenuOnSelect={false}
                  placeholder="Select or create services"
                />
              </div>
            )}
            {showBusinessFields && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Services
                </label>
                <AsyncCreatableSelect
                  isClearable
                  isMulti
                  cacheOptions
                  defaultOptions={serviceDefaults}
                  loadOptions={debouncedLoadService}
                  components={{ Option: DeletableAsyncOption }}
                  userPermission={userPermission}
                  onDeleteOption={handleDeleteServiceOption}
                  deletingOptionId={deletingServiceId}
                  value={selectedCompanyServiceValues}
                  onChange={(selected) =>
                    setFormData((prev) => ({
                      ...prev,
                      services_company_ids: (selected || []).map(
                        (o) => o.value,
                      ),
                      services_company: (selected || []).map((o) => o.label),
                    }))
                  }
                  onCreateOption={async (name) => {
                    try {
                      const created = await leadService.createService(name);
                      const opt = { value: created.id, label: created.name };
                      setServiceDefaults((prev) => [...prev, opt]);
                      setFormData((prev) => ({
                        ...prev,
                        services_company_ids: [
                          ...(prev.services_company_ids || []),
                          created.id,
                        ],
                        services_company: [
                          ...(prev.services_company || []),
                          created.name,
                        ],
                      }));
                    } catch (e) {
                      console.error(e);
                      toast.error("Could not create Service");
                    }
                  }}
                  closeMenuOnSelect={false}
                  placeholder="Select or create services"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Did you have an Accountant?
              </label>
              <select
                name="accountant"
                value={formData.accountant}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Select option</option>
                {constantValues.accountantOptions.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-red-600 mb-2">
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Secondary Email
              </label>
              <input
                type="email"
                name="secondary_email"
                value={formData.secondary_email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-red-600 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Phone Numbers
              </label>
              <input
                type="tel"
                name="additional_phones"
                value={formData.additional_phones}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Method of Contact
              </label>
              <select
                name="contact_method"
                value={formData.contact_method}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Select method</option>
                {constantValues.contactMethodOptions.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Foreign
              </label>
              <select
                name="international"
                value={formData.international}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Select option</option>
                {constantValues.internationalOptions.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Referral
              </label>
              <select
                name="referral"
                value={formData.referral}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Select option</option>
                {constantValues.referralOptions.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Refer Partner
              </label>
              <AsyncCreatableSelect
                isClearable
                cacheOptions
                defaultOptions={referPartnersOptions}
                loadOptions={debouncedLoadRefer}
                components={{ Option: DeletableAsyncOption }}
                userPermission={userPermission}
                onDeleteOption={handleDeleteReferPartnerOption}
                deletingOptionId={deletingReferPartnerId}
                value={selectedReferPartnerOption}
                onChange={(option) =>
                  setFormData((prev) => ({
                    ...prev,
                    refer_partner_id: option ? option.value : null,
                    refer_partner_name: option ? option.label : "",
                  }))
                }
                onCreateOption={async (name) => {
                  try {
                    const created = await leadService.createReferPartner(name);
                    const opt = { value: created.id, label: created.name };
                    setReferPartnerDefaults((prev) => [...prev, opt]);
                    setFormData((prev) => ({
                      ...prev,
                      refer_partner_id: created.id,
                    }));
                  } catch (e) {
                    console.error(e);
                    toast.error("Could not create Refer Partner");
                  }
                }}
                placeholder="Select or create a refer partner"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-red-600 mb-2">
                Sales Representative *
              </label>
              <AsyncCreatableSelect
                isClearable
                cacheOptions
                defaultOptions={salesRepOptions}
                loadOptions={debouncedLoadSales}
                components={{ Option: DeletableAsyncOption }}
                userPermission={userPermission}
                onDeleteOption={handleDeleteSalesRepOption}
                deletingOptionId={deletingSalesRepId}
                value={selectedSalesRepOption}
                onChange={(option) =>
                  setFormData((prev) => ({
                    ...prev,
                    sales_rep_id: option ? option.value : null,
                    sales_rep_name: option ? option.label : "",
                  }))
                }
                onCreateOption={async (name) => {
                  try {
                    const created = await clientService.createSalesRep(name);
                    const opt = { value: created.id, label: created.name };
                    setSalesRepDefaults((prev) => [...prev, opt]);
                    setFormData((prev) => ({
                      ...prev,
                      sales_rep_id: created.id,
                    }));
                  } catch (e) {
                    console.error(e);
                    toast.error("Could not create Sales Rep");
                  }
                }}
                placeholder="Select or create a sales rep"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Language
              </label>
              <select
                name="language"
                value={formData.language}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Select language</option>
                {constantValues.languageOptions.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex items-center justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate("/leads")}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={loading}
            className="flex items-center px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="spinner mr-2">s</div>
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {loading ? "Creating..." : "Create Lead"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateLead;
